import React, { useState } from "react";
import { Mail, MapPin, MessageSquare, Send, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../lib/api";
import { contactInfo } from "../mock/mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast({ title: "Message sent", description: "Thanks for reaching out. Ryan will get back to you within 24 hours." });
    } catch (err) {
      toast({ title: "Something went wrong", description: err?.response?.data?.detail || "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-ep-royal py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">Contact</div>
          <h1 className="text-white text-4xl sm:text-5xl font-bold mt-3">Get in touch with Ryan</h1>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            Have a question about the picks, membership, or a technical issue?
            Ryan responds personally, usually within one business day.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <ContactInfoCard icon={Mail} title="Email" value={contactInfo.email} hint="For all general enquiries" />
          <ContactInfoCard icon={MessageSquare} title="Member Support" value={contactInfo.supportEmail} hint="For subscription questions" />
          <ContactInfoCard icon={MapPin} title="Based in" value={contactInfo.location} hint="Serving members worldwide" />
          <ContactInfoCard icon={Clock} title="Response time" value="Under 24 hours" hint="Monday to Sunday" />
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 shadow-sm">
          <h2 className="text-[#0a1a4a] text-2xl font-bold">Send Ryan a message</h2>
          <p className="text-[#0a1a4a]/60 text-sm mt-1">Fill out the form below and Ryan will be in touch shortly.</p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Your name"><Input required value={form.name} onChange={set("name")} className="h-11" placeholder="Jane Doe" /></Field>
              <Field label="Email address"><Input required type="email" value={form.email} onChange={set("email")} className="h-11" placeholder="you@example.com" /></Field>
            </div>
            <Field label="Subject"><Input required value={form.subject} onChange={set("subject")} className="h-11" placeholder="How can Ryan help?" /></Field>
            <Field label="Message"><Textarea required value={form.message} onChange={set("message")} rows={6} placeholder="Tell Ryan more..." /></Field>
            <Button type="submit" disabled={loading} className="bg-ep-coral hover:bg-[#ff7676] text-white h-12 px-6 font-semibold coral-glow">
              {loading ? "Sending..." : (<>Send message <Send size={16} className="ml-2" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[#0a1a4a] text-sm font-semibold mb-1.5 block">{label}</label>
    {children}
  </div>
);

const ContactInfoCard = ({ icon: Icon, title, value, hint }) => (
  <div className="card-lift bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-4">
    <div className="w-11 h-11 rounded-xl bg-[#ff5a5a]/10 flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-[#ff5a5a]" />
    </div>
    <div className="min-w-0">
      <div className="text-[#0a1a4a]/60 text-xs font-semibold uppercase tracking-wider">{title}</div>
      <div className="text-[#0a1a4a] font-semibold mt-0.5 break-all">{value}</div>
      <div className="text-[#0a1a4a]/60 text-xs mt-1">{hint}</div>
    </div>
  </div>
);

export default Contact;
